import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageTopNav } from '@/components/nav/PageTopNav';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, ArrowLeft, X } from 'lucide-react';
import { addOrgSpeciesSchema } from '@tyto/shared';
import SpeciesSearch, { type Species } from '@/components/SpeciesSearch';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import { useParams } from 'react-router';

type AddOrgSpeciesForm = z.infer<typeof addOrgSpeciesSchema>;

const AddOrgSpecies: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);
  const { orgId } = useParams<{ orgId: string }>();

  // Form setup
  const form = useForm<AddOrgSpeciesForm>({
    resolver: zodResolver(addOrgSpeciesSchema),
    defaultValues: {
      speciesId: '',
      status: "RESIDENT",
      note: ''
    },
  });

  // Create record mutation
  const createRecordMutation = useMutation({
    mutationFn: async (data: AddOrgSpeciesForm) => {
      const response = await fetch(`/api/org/${orgId}/species`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create record');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationSpecies'] });
      navigate(`/app/org/${orgId}/species`);
    },
  });

  // Form submission handler
  const onSubmit = (data: AddOrgSpeciesForm) => {
    createRecordMutation.mutate(data);
  };

  return (
    <div className="container mx-auto">
      <PageTopNav pageName="Add Species" />

      <div className="mb-4">
        <h2 className="text-xl font-semibold">Species Details</h2>
        <p className="text-sm text-muted-foreground">
          Add a new species to your organization.
        </p>
      </div>
      <div className="mb-4">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="speciesId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Species</FormLabel>
                  <FormControl>
                    <SpeciesSearch
                      onSelect={(species: Species) => {
                        setSelectedSpecies(species);
                        field.onChange(species.speciesId || '');
                      }}
                      placeholder="Search for a species..."
                    />
                  </FormControl>
                  {selectedSpecies && (
                    <div className="mt-2 p-2 bg-secondary rounded-md flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{selectedSpecies.englishName}</p>
                        <p className="text-sm italic">{selectedSpecies.scientificName}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedSpecies(null);
                          field.onChange('');
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={x => field.onChange(x)}>
                      <SelectTrigger>{field.value}</SelectTrigger>
                      <SelectContent>
                        <SelectItem value='RESIDENT'>RESIDENT</SelectItem>
                        <SelectItem value='ANNUAL'>ANNUAL</SelectItem>
                        <SelectItem value='UNCOMMON'>UNCOMMON</SelectItem>
                        <SelectItem value='VAGRANT'>VAGRANT</SelectItem>
                        <SelectItem value='SCARCE'>SCARCE</SelectItem>
                        <SelectItem value='RARE'>RARE</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Note</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      placeholder="Additional notes about the species"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>
      <div className="flex flex-row items-center gap-2">
        <Button variant="outline" onClick={() => navigate('/app/records')}>
          Cancel
        </Button>
        <Button
          onClick={form.handleSubmit(onSubmit)}
          disabled={createRecordMutation.isPending}
        >
          {createRecordMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Add Species
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AddOrgSpecies;
